import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "customerSearch",
    "customerResults",
    "customerIdInput",
    "selectedCustomer",
    "customerMemo",
    "customerMemoContent",
    "reservationSelect",
    "reservationIdInput",
    "reservationCards",
    "reservationSelectionArea",
    "periodFilter",
    "categorySelect",
    "exerciseSelect",
    "reservationInfo",
    "exerciseItems",
    "exerciseItem"
  ]

  connect() {
    console.log("Training record form controller connected")
    this.searchTimer = null
    this.exerciseIndex = 1 // fields_forのインデックス管理

    // 初期状態で顧客が選択されている場合の処理
    if (this.customerIdInputTarget.value) {
      this.updateReservations(this.customerIdInputTarget.value)
    }
  }

  // 手動入力時に予約IDをクリア
  clearReservation() {
    this.reservationIdInputTarget.value = ""
    // 予約カードの選択状態をクリア
    this.reservationCardsTargets.forEach(card => {
      card.classList.remove("selected")
    })
  }

  // 顧客検索（入力時）
  searchCustomers() {
    console.log("searchCustomers called")
    clearTimeout(this.searchTimer)
    const query = this.customerSearchTarget.value.trim()
    console.log("Query:", query)

    if (query.length < 1) {
      this.customerResultsTarget.innerHTML = ""
      this.customerResultsTarget.classList.add("d-none")
      return
    }

    this.searchTimer = setTimeout(async () => {
      try {
        const response = await fetch(`/admin/training_records/search_customers?q=${encodeURIComponent(query)}`)
        const data = await response.json()

        if (data.customers.length > 0) {
          this.customerResultsTarget.innerHTML = data.customers.map(customer => `
            <div class="customer-search-item p-2 border-bottom"
                 role="button"
                 data-customer-id="${customer.id}"
                 data-customer-name="${customer.display_name}"
                 data-customer-memo="${customer.customer_memo || ''}"
                 data-action="click->training-record-form#selectCustomer">
              <div class="fw-semibold">${customer.display_name}</div>
              <small class="text-muted">${customer.email}</small>
            </div>
          `).join('')
          this.customerResultsTarget.classList.remove("d-none")
        } else {
          this.customerResultsTarget.innerHTML = '<div class="p-3 text-muted text-center">顧客が見つかりません</div>'
          this.customerResultsTarget.classList.remove("d-none")
        }
      } catch (error) {
        console.error('Error searching customers:', error)
      }
    }, 300)
  }

  // 顧客を選択
  selectCustomer(event) {
    const customerId = event.currentTarget.dataset.customerId
    const customerName = event.currentTarget.dataset.customerName
    const customerMemo = event.currentTarget.dataset.customerMemo

    this.customerIdInputTarget.value = customerId
    this.customerSearchTarget.value = customerName
    this.selectedCustomerTarget.innerHTML = `
      <div class="alert alert-success py-2">
        <i class="bi bi-check-circle me-2"></i>
        選択中: <strong>${customerName}</strong>
      </div>
    `
    this.customerResultsTarget.innerHTML = ""
    this.customerResultsTarget.classList.add("d-none")

    // 顧客メモを表示
    if (customerMemo && customerMemo.trim() !== '') {
      this.customerMemoContentTarget.textContent = customerMemo
      this.customerMemoTarget.classList.remove("d-none")
    } else {
      this.customerMemoTarget.classList.add("d-none")
    }

    this.updateReservations(customerId)
  }

  // 顧客が選択されたときに予約リストを更新
  async updateReservations(customerId) {
    if (!customerId) {
      this.reservationSelectionAreaTarget.innerHTML = `
        <div class="alert alert-warning">
          <i class="bi bi-info-circle me-2"></i>
          先に顧客を選択してください
        </div>
      `
      return
    }

    try {
      const response = await fetch(`/admin/training_records/customer_reservations?customer_id=${customerId}`)
      const data = await response.json()

      if (data.reservations.length === 0) {
        this.reservationSelectionAreaTarget.innerHTML = `
          <div class="alert alert-warning">
            <i class="bi bi-info-circle me-2"></i>
            この顧客の予約履歴がありません
          </div>
        `
        return
      }

      // 期間フィルターとカード形式の予約リストを生成
      let html = `
        <div class="btn-group mb-3" role="group" data-training-record-form-target="periodFilter">
          <button class="btn btn-outline-secondary active" type="button" data-period="2" data-action="click->training-record-form#filterReservations">
            過去2ヶ月
          </button>
          <button class="btn btn-outline-secondary" type="button" data-period="1" data-action="click->training-record-form#filterReservations">
            過去1ヶ月
          </button>
          <button class="btn btn-outline-secondary" type="button" data-period="3" data-action="click->training-record-form#filterReservations">
            過去3ヶ月
          </button>
          <button class="btn btn-outline-secondary" type="button" data-period="all" data-action="click->training-record-form#filterReservations">
            全て
          </button>
        </div>

        <div class="reservation-cards" data-training-record-form-target="reservationCards">
      `

      // 曜日の日本語配列
      const weekdays = ['日', '月', '火', '水', '木', '金', '土']

      data.reservations.forEach(reservation => {
        // datetimeは既に日本語形式なので、正規表現でパース
        const match = reservation.datetime.match(/(\d{4})年(\d{1,2})月(\d{1,2})日 (\d{2}):(\d{2})/)
        if (!match) {
          console.error('Invalid datetime format:', reservation.datetime)
          return
        }

        const date = new Date(match[1], match[2] - 1, match[3], match[4], match[5])
        const weekday = weekdays[date.getDay()]
        const dateStr = `${match[2]}月${match[3]}日`
        const timeStr = `${match[4]}:${match[5]}`

        html += `
          <div class="reservation-card border rounded p-3 mb-2 cursor-pointer"
               data-reservation-id="${reservation.id}"
               data-reservation-date="${date.toISOString().split('T')[0]}"
               data-action="click->training-record-form#selectReservation">
            <div class="row align-items-center">
              <div class="col-md-3">
                <div class="h5 mb-0 text-primary">${dateStr}</div>
                <div class="small text-muted">(${weekday}) ${timeStr}</div>
              </div>
              <div class="col-md-5">
                <div class="fw-semibold">${reservation.menu_name}</div>
                <div class="small text-muted">
                  <i class="bi bi-person me-1"></i>
                  ${reservation.trainer_name}
                </div>
              </div>
              <div class="col-md-4 text-end">
              </div>
            </div>
          </div>
        `
      })

      html += '</div>'
      this.reservationSelectionAreaTarget.innerHTML = html

      // 初期フィルタリング（過去2ヶ月）を適用
      setTimeout(() => {
        const defaultButton = this.periodFilterTarget.querySelector('[data-period="2"]')
        if (defaultButton) {
          defaultButton.click()
        }
      }, 100)

    } catch (error) {
      console.error('Error fetching reservations:', error)
    }
  }

  // 予約カードを選択
  selectReservation(event) {
    const card = event.currentTarget
    const reservationId = card.dataset.reservationId

    // 他のカードの選択を解除
    this.element.querySelectorAll('.reservation-card').forEach(c => {
      c.classList.remove('selected', 'bg-light')
    })

    // 選択したカードをハイライト
    card.classList.add('selected', 'bg-light')

    // 隠しフィールドに値を設定
    this.reservationIdInputTarget.value = reservationId

    // 手動入力フィールドをクリア（もしあれば）
    const trainingDateField = this.element.querySelector('input[name="training_record[training_date]"]')
    if (trainingDateField) {
      trainingDateField.value = ''
    }
  }

  // 期間フィルター
  filterReservations(event) {
    const button = event.currentTarget
    const period = button.dataset.period

    // ボタンのアクティブ状態を切り替え
    this.periodFilterTarget.querySelectorAll('button').forEach(btn => {
      btn.classList.remove('active')
    })
    button.classList.add('active')

    // カードをフィルタリング
    const now = new Date()
    const cards = this.reservationCardsTarget.querySelectorAll('.reservation-card')

    cards.forEach(card => {
      const cardDate = new Date(card.dataset.reservationDate)
      const monthsAgo = (now.getFullYear() - cardDate.getFullYear()) * 12 + (now.getMonth() - cardDate.getMonth())

      let shouldShow = false
      switch(period) {
        case '1':
          shouldShow = monthsAgo <= 1
          break
        case '2':
          shouldShow = monthsAgo <= 2
          break
        case '3':
          shouldShow = monthsAgo <= 3
          break
        case 'all':
          shouldShow = true
          break
      }

      card.style.display = shouldShow ? '' : 'none'
    })
  }

  // 予約が選択されたときに詳細情報を表示
  updateReservationInfo() {
    // 旧実装との互換性のため残す
  }

  // カテゴリが選択されたときにエクササイズリストを更新（個別のエクササイズアイテム用）
  updateExerciseOptions(event) {
    const categorySelect = event.currentTarget
    const exerciseItem = categorySelect.closest('.exercise-item')
    const exerciseSelect = exerciseItem.querySelector('[data-training-record-form-target="exerciseSelect"]')
    const category = categorySelect.value

    if (!category) {
      exerciseSelect.innerHTML = '<option value="">先にカテゴリを選択してください</option>'
      exerciseSelect.disabled = true
      return
    }

    // エクササイズデータを取得（Stimulusのvalueから）
    const exercisesData = this.data.has('exercisesValue') ? JSON.parse(this.data.get('exercisesValue')) : {}
    const exercises = exercisesData[category] || []

    exerciseSelect.innerHTML = '<option value="">エクササイズを選択してください</option>'

    exercises.forEach(exercise => {
      const option = document.createElement('option')
      option.value = exercise.id
      option.textContent = exercise.name
      exerciseSelect.appendChild(option)
    })

    exerciseSelect.disabled = false
  }

  // エクササイズを追加
  addExercise(event) {
    event.preventDefault()

    const template = this.createExerciseItemTemplate()
    this.exerciseItemsTarget.insertAdjacentHTML('beforeend', template)
    this.exerciseIndex++
  }

  // エクササイズを削除
  removeExercise(event) {
    event.preventDefault()

    const exerciseItem = event.currentTarget.closest('.exercise-item')

    // _destroyフィールドがある場合（既存のレコード）
    const destroyField = exerciseItem.querySelector('input[name*="_destroy"]')
    if (destroyField) {
      destroyField.value = '1'
      exerciseItem.style.display = 'none'
    } else {
      // 新規追加されたアイテムの場合は完全に削除
      exerciseItem.remove()
    }
  }

  // エクササイズアイテムのテンプレートを作成
  createExerciseItemTemplate() {
    const timestamp = new Date().getTime()

    return `
      <div class="exercise-item border rounded p-3 mb-3" data-training-record-form-target="exerciseItem">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <h6 class="mb-0">エクササイズ</h6>
          <button class="btn btn-link text-danger p-0 ms-auto" type="button" style="font-size: 1rem; width: auto;" data-action="click->training-record-form#removeExercise">
            <i class="bi bi-trash"></i>
          </button>
        </div>

        <div class="mb-3">
          <label class="form-label">
            <i class="bi bi-folder me-1"></i>
            カテゴリ
          </label>
          <select class="form-select exercise-category-select"
                  data-training-record-form-target="categorySelect"
                  data-action="change->training-record-form#updateExerciseOptions">
            <option value="">カテゴリを選択</option>
            <option value="conditioning">コンディショニング</option>
            <option value="legs">脚</option>
            <option value="chest">胸</option>
            <option value="back">背中</option>
            <option value="shoulders">肩</option>
            <option value="arms">腕</option>
            <option value="abs">腹筋</option>
          </select>
        </div>

        <div class="mb-3">
          <label class="form-label" for="training_record_training_record_items_attributes_${timestamp}_exercise_id">
            <i class="bi bi-activity me-1"></i>
            エクササイズ
          </label>
          <select class="form-select"
                  disabled="disabled"
                  required="required"
                  data-training-record-form-target="exerciseSelect"
                  id="training_record_training_record_items_attributes_${timestamp}_exercise_id"
                  name="training_record[training_record_items_attributes][${timestamp}][exercise_id]">
            <option value="">先にカテゴリを選択してください</option>
          </select>
        </div>

        <div class="row">
          <div class="col-md-4 mb-3">
            <label class="form-label" for="training_record_training_record_items_attributes_${timestamp}_weight">
              <i class="bi bi-speedometer2 me-1"></i>
              重量 (kg)
            </label>
            <div class="input-group">
              <input class="form-control"
                     step="0.5"
                     min="0"
                     type="number"
                     name="training_record[training_record_items_attributes][${timestamp}][weight]"
                     id="training_record_training_record_items_attributes_${timestamp}_weight">
              <span class="input-group-text">kg</span>
            </div>
          </div>

          <div class="col-md-4 mb-3">
            <label class="form-label" for="training_record_training_record_items_attributes_${timestamp}_reps">
              <i class="bi bi-arrow-repeat me-1"></i>
              回数
            </label>
            <div class="input-group">
              <input class="form-control"
                     min="1"
                     type="number"
                     name="training_record[training_record_items_attributes][${timestamp}][reps]"
                     id="training_record_training_record_items_attributes_${timestamp}_reps">
              <span class="input-group-text">回</span>
            </div>
          </div>

          <div class="col-md-4 mb-3">
            <label class="form-label" for="training_record_training_record_items_attributes_${timestamp}_sets">
              <i class="bi bi-stack me-1"></i>
              セット数
            </label>
            <div class="input-group">
              <input class="form-control"
                     min="1"
                     type="number"
                     name="training_record[training_record_items_attributes][${timestamp}][sets]"
                     id="training_record_training_record_items_attributes_${timestamp}_sets">
              <span class="input-group-text">セット</span>
            </div>
          </div>
        </div>

        <div class="mb-3">
          <div class="form-check form-switch">
            <input class="form-check-input"
                   type="checkbox"
                   value="1"
                   name="training_record[training_record_items_attributes][${timestamp}][assisted]"
                   id="training_record_training_record_items_attributes_${timestamp}_assisted">
            <label class="form-check-label" for="training_record_training_record_items_attributes_${timestamp}_assisted">
              <i class="bi bi-hand-thumbs-up me-1"></i>
              アシスト付き
            </label>
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label" for="training_record_training_record_items_attributes_${timestamp}_notes">
            <i class="bi bi-chat-left-text me-1"></i>
            メモ
          </label>
          <textarea class="form-control"
                    rows="2"
                    placeholder="このエクササイズに関するメモ"
                    name="training_record[training_record_items_attributes][${timestamp}][notes]"
                    id="training_record_training_record_items_attributes_${timestamp}_notes"></textarea>
        </div>
      </div>
    `
  }
}
